// ============================================================================
// Configuración central de tipos de Comprobantes Fiscales Electrónicos (e-CF)
// ============================================================================
// FUENTE ÚNICA DE VERDAD. Los tipos de comprobantes NO deben repetirse en
// componentes individuales; cualquier pantalla debe consumirlos desde aquí.
//
// Códigos y denominaciones conforme a la normativa DGII de República
// Dominicana (serie "E" de comprobantes electrónicos).
// ============================================================================

export interface ElectronicDocumentType {
  /** Código numérico del tipo de comprobante (p. ej. "31"). */
  code: string;
  /** Nombre oficial del comprobante. */
  name: string;
  /** Descripción breve para pantallas y listados. */
  description: string;
  /** Si el tipo está habilitado para emisión. */
  active: boolean;
  /** Si requiere un cliente/comprador asociado. */
  requires_customer: boolean;
  /** Si exige RNC/Cédula del receptor. */
  requires_rnc: boolean;
  /** Si otorga crédito fiscal al receptor. */
  allows_credit: boolean;
  /** Si permite calcular impuestos (ITBIS). */
  allows_tax: boolean;
  /** Si permite referenciar un comprobante anterior (notas). */
  allows_reference: boolean;
  /** Si lleva detalle de ítems. */
  allows_items: boolean;
  /** Versión de la configuración del tipo. */
  version: string;
}

export const ELECTRONIC_DOCUMENT_TYPES: ElectronicDocumentType[] = [
  {
    code: '31',
    name: 'Factura de Crédito Fiscal Electrónica',
    description:
      'Acredita operaciones gravadas y da derecho a crédito fiscal al comprador.',
    active: true,
    requires_customer: true,
    requires_rnc: true,
    allows_credit: true,
    allows_tax: true,
    allows_reference: false,
    allows_items: true,
    version: '1.0',
  },
  {
    code: '32',
    name: 'Factura de Consumo Electrónica',
    description:
      'Comprobante para consumidor final que no da derecho a crédito fiscal.',
    active: true,
    requires_customer: false,
    requires_rnc: false,
    allows_credit: false,
    allows_tax: true,
    allows_reference: false,
    allows_items: true,
    version: '1.0',
  },
  {
    code: '33',
    name: 'Nota de Débito Electrónica',
    description:
      'Incrementa el valor de un comprobante emitido anteriormente.',
    active: true,
    requires_customer: true,
    requires_rnc: true,
    allows_credit: true,
    allows_tax: true,
    allows_reference: true,
    allows_items: true,
    version: '1.0',
  },
  {
    code: '34',
    name: 'Nota de Crédito Electrónica',
    description:
      'Disminuye el valor de un comprobante emitido anteriormente.',
    active: true,
    requires_customer: true,
    requires_rnc: true,
    allows_credit: true,
    allows_tax: true,
    allows_reference: true,
    allows_items: true,
    version: '1.0',
  },
  {
    code: '41',
    name: 'Compras Electrónico',
    description:
      'Comprobante de compras de bienes y servicios recibidos.',
    active: true,
    requires_customer: false,
    requires_rnc: false,
    allows_credit: true,
    allows_tax: true,
    allows_reference: false,
    allows_items: true,
    version: '1.0',
  },
  {
    code: '43',
    name: 'Gastos Menores Electrónico',
    description:
      'Comprobante para gastos menores no significativos.',
    active: true,
    requires_customer: false,
    requires_rnc: false,
    allows_credit: false,
    allows_tax: true,
    allows_reference: false,
    allows_items: true,
    version: '1.0',
  },
];

/** Mapa de acceso rápido por código. */
export const ELECTRONIC_DOCUMENT_TYPES_BY_CODE: Record<
  string,
  ElectronicDocumentType
> = Object.fromEntries(
  ELECTRONIC_DOCUMENT_TYPES.map((t) => [t.code, t])
);

export function getDocumentType(code: string): ElectronicDocumentType | undefined {
  return ELECTRONIC_DOCUMENT_TYPES_BY_CODE[code];
}

export function getDocumentTypeName(code: string): string {
  return getDocumentType(code)?.name ?? `Comprobante ${code}`;
}

export function getActiveDocumentTypes(): ElectronicDocumentType[] {
  return ELECTRONIC_DOCUMENT_TYPES.filter((t) => t.active);
}